import axios from 'axios'
import { setupMobileInterceptors } from './mobileAuthService.js'
import { USE_JWT_COOKIE } from '../../shared/auth/authConfig.js'

/**
 * APIベースURL
 * モバイルシェア用のAPIエンドポイント
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

/**
 * モバイルシェア用のaxiosインスタンス
 * 共通の設定とインターセプターが適用されたHTTPクライアント
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// モバイルシェア用のインターセプターを設定
setupMobileInterceptors(apiClient, 'Mobile Share')

/**
 * シェア用オブジェクト数を取得
 * 指定されたオブジェクトのシェア用データ数を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} オブジェクト数データ
 */
const getObjectCountForShare = async (objectId, pixId) => {
  const data = {
    objectId: objectId,
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getObjectCount', data);
  return response.data;
}

/**
 * シェア用オブジェクト詳細情報を取得
 * 指定されたオブジェクトのシェア用詳細情報を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} categoryId - 撮影メモカテゴリーID（オプション）
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} オブジェクト詳細情報
 */
const getObjectDetailForShare = async (objectId, categoryId, pixId) => {
  const data = {
    objectId: objectId,
    zsatsueiMemoCategoryId: categoryId,
    changeSatsueiMemoFlg: (categoryId ? true : false),
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };

  const response = await apiClient.post('/getObject', data);
  return response.data;
}

/**
 * シェア用オブジェクト画像一覧を取得
 * 指定されたオブジェクトのシェア用画像一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} 画像一覧データ
 */
const getObjectImageListForShare = async(objectId, pixId) => {
  const data = {
    objectId: objectId,
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  
  const response = await apiClient.post('/getObjectImageList', data);
  return response.data;
}

/**
 * シェア用撮影メモカテゴリー一覧を取得
 * シェア用の撮影メモカテゴリー一覧を取得
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} カテゴリー一覧データ
 */
const getSatsueiMemoCategories = async (pixId) => {
  const data = {
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getSatsueiMemoCategory', data);
  return response.data;
}

/**
 * シェア用撮影ガイド一覧を取得
 * 指定されたカテゴリーのシェア用撮影ガイド一覧を取得
 * @param {string} categoryId - 撮影メモカテゴリーID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} 撮影ガイド一覧データ
 */
const getSatsueiGuides = async (categoryId, pixId) => {
  const data = {
    satsueiMemoCategoryId: categoryId,
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getSatsueiGuideList', data);
  return response.data;
}

/**
 * シェア用拠点一覧を取得
 * シェア用の拠点一覧を取得
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} 拠点一覧データ
 */
const getKyotenLists = async (pixId) => {
  const data = {
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getKyotenList', data);
  return response.data;
}

/**
 * シェア用保存フォルダ一覧を取得
 * シェア用の画像保存フォルダー一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} フォルダー一覧データ
 */
const getImageFolders = async (objectId, pixId) => {
  const data = {
    objectId: objectId,
    isContainMishitei: true,  // 未指定フォルダーを含むフラグ
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getImageFolderList', data);
  return response.data;
}

/**
 * シェア用動画一覧を取得
 * 指定されたオブジェクトのシェア用動画一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} 動画一覧データ
 */
const getMovieListForShare = async (objectId, pixId) => {
  const data = {
    objectId: objectId,
    isShareFlg: true,        // シェアフラグ
    sharePixId: pixId        // シェア用PIX ID
  };
  const response = await apiClient.post('/getMovieList', data);
  return response.data;
}

/**
 * シェア情報を取得
 * 指定されたシェアコードのシェア情報を取得
 * @param {string} shareCode - シェアコード
 * @returns {Promise<Object>} シェア情報データ
 */
const getShareInfo = async (shareCode) => {
  const data = {
    shareCode: shareCode
  };
  const response = await apiClient.post('/getShareInfo', data);
  return response.data;
}

/**
 * シェア用画像タグ一覧を取得
 * 指定された画像のシェア用タグ一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @param {string} pixId - PIX ID
 * @returns {Promise<Object>} タグ一覧データ
 */
const getObjectImageTag = async (objectId, objectImageId, pixId) => {
  const data = {
    objectId: objectId,
    objectImageId: objectImageId,
    pixId: pixId
  };
  const response = await apiClient.post('/getObjectImageTag', data);
  return response.data;
}

/**
 * シェア用画像をblobとして取得
 * 指定された画像をblob形式で取得し、URLオブジェクトとして返す
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @param {string} objectImageEditId - 画像編集ID
 * @param {string} filenameExtension - ファイル拡張子
 * @param {boolean} isUploaded - アップロード済みフラグ
 * @param {string} pixId - PIX ID
 * @returns {Promise<string>} 画像URLまたはエラー画像パス
 */
const getImageAsBlob = async (objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded, pixId) => {
  if (objectImageId === undefined || objectImageId === null || objectImageId === '') {
    return '/core/img/errorimage.png'
  }

  try {
    const response = await apiClient.get('/getImage', {
      params: {
        objectId,
        objectImageId,
        objectImageEditId,
        filenameExtension,
        isUploaded,
        type: 'thumb_comp',    // サムネイル圧縮タイプ
        width: 148,            // 幅
        height: 148,           // 高さ
        isShareFlg: true,      // シェアフラグ
        sharePixId: pixId      // シェア用PIX ID
      },
      responseType: 'blob',
      headers: { Accept: 'image/jpeg' }
    })
    return URL.createObjectURL(response.data)
  } catch (error) {
    return '/core/img/errorimage.png'
  }
}

/**
 * モバイルシェアサービス
 * シェア用のオブジェクト情報取得、メタデータ取得、シェア情報管理などのシェア関連機能を提供
 */
export const mobileShareService = {
  // === オブジェクト情報取得 ===
  getObjectCountForShare,      // シェア用オブジェクト数取得
  getObjectDetailForShare,     // シェア用オブジェクト詳細取得
  getObjectImageListForShare,  // シェア用オブジェクト画像一覧取得
  getMovieListForShare,        // シェア用動画一覧取得

  // === メタデータ取得 ===
  getSatsueiMemoCategories,    // シェア用撮影メモカテゴリー取得
  getSatsueiGuides,            // シェア用撮影ガイド取得
  getKyotenLists,              // シェア用拠点一覧取得
  getImageFolders,             // シェア用保存フォルダ一覧取得

  // === シェア・タグ機能 ===
  getShareInfo,                // シェア情報取得
  getObjectImageTag,           // シェア用画像タグ一覧取得

  // === 画像取得 ===
  getImageAsBlob,              // シェア用画像をblobとして取得
}
